import xml2js, { ParserOptions } from 'xml2js';
import { parse } from 'qs';
import he from 'he';
import CryptoJS from 'crypto-js';
import { parseBooleans, parseNumbers } from 'xml2js/lib/processors';
import axios, { AxiosResponse } from 'axios';
import FileSaver from 'file-saver';
import { clone, isFunction } from 'lodash-es';
import BoolProps from '../JSApi/constants/BooleanProps';
import NumberProps from '../JSApi/constants/NumberProps';
import Field from '../JSApi/constants/Field';
import type {
  TApiaFieldPropsObj,
  TFieldScriptEvents,
  TFieldServerEvents,
  TMap,
  TSessionExceptions,
} from '../types';
import store from '../store';
import { updateSessionState } from '../store/appStateSlice';
import { TApiaFieldValuesObj } from '../store/valuesSlice';
import ApiaApi from '../api/apiaApi/apiaApi';
import propsStore from '../store/propsStore/propsStore';
import { dispatchNotifications } from '../components/common/notifications/util';

export const getComposedID = (
  formId: string,
  id: string,
  onGrid: boolean,
  index: number,
): string => {
  if (!onGrid) return `${formId}_${id}`;
  return `${formId}_${id}_${index}`;
};

export const obj2Arr = <T extends object>(obj: T): T[keyof T][] => {
  const result = [];
  const keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i += 1) {
    result.push(obj[keys[i] as keyof T]);
  }
  return result;
};

export const isEmptyObj = <T>(obj: T): boolean => {
  return (
    obj &&
    Object.keys(obj).length === 0 &&
    Object.getPrototypeOf(obj) === Object.prototype
  );
};

export const isArrayOfStrings = <T>(array: T[]): boolean => {
  return (
    Array.isArray(array) && array.every((item) => typeof item === 'string')
  );
};

export const convertToArray = <T>(arrayOrObject: T | T[] | undefined): T[] => {
  if (arrayOrObject !== undefined) {
    if (Array.isArray(arrayOrObject)) {
      return arrayOrObject;
    }
    return [arrayOrObject];
  }
  return [];
};

// get JS object from string
export const jsonDecode = (string: string): TMap<string | boolean | number> => {
  if (!string || typeof string !== 'string') return {};
  // remove eol enters
  const str = string.replace(/\n|\r/g, '');
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return eval(`(${str})`);
};

// get boolean value from string values
export const parseBool = (val: string | null): boolean => {
  if (typeof val === 'string') {
    if (/^(?:true|T|t|1|1,00)$/i.test(val)) return true;
    if (/^(?:false|F|f|0|0,00|null)$/i.test(val)) return false;
  }
  return false;
};

// unescape html string
export const unescapeMarkup = (str: string): string => {
  if (str !== undefined && str.length > 0) {
    return str
      .replace(/&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&gt;/g, '>')
      .replace(/&lt;/g, '<')
      .replace(/&amp;/g, '&');
  }
  return '';
};

type TFieldProps = TMap<string | boolean | number>;
type TFieldValueProps = (string | boolean | number)[];
type TFieldParsedProps = TMap<string | boolean | number>;

// parse XML properties of Field element
export const parseFieldPropertiesObj = (
  props: TFieldProps,
): TFieldParsedProps => {
  // default values for missing props can go here below in response.
  const response: TFieldParsedProps = {};
  const objKeys: string[] = Object.keys(props);
  const objVal: TFieldValueProps = Object.values(props);
  for (let i = 0; i < objKeys.length; i += 1) {
    const key = objKeys[i];
    if (BoolProps.indexOf(key) === -1 && NumberProps.indexOf(key) === -1) {
      response[key] = objVal[i] || '';
    } else if (typeof objVal[i] === 'number') {
      response[key] = objVal[i];
    } else if (BoolProps.indexOf(key) !== -1) {
      response[key] = parseBool(objVal[i] as string);
    } else if (NumberProps.indexOf(key) !== -1) {
      response[key] = parseInt((objVal[i] as string) || '', 10);
    }
  }
  return response;
};

const processAjaxEventTagNames = (name: string): string => {
  if (name === 'a') return 'form';
  if (name === 'b') return 'field';
  if (name === 'g') return 'possibleValues';
  if (name === 'h') return 'item';
  if (name === 'z') return 'focusTo';
  if (name === 'r') return 'reload';
  if (name === 'p') return 'proccess';
  if (name === 'd') return 'valueAttr';
  return name;
};

const processAjaxEventAttrNames = (name: string): string => {
  if (name === 't') return 'text';
  if (name === 'label') return 'LABEL';
  return name;
};

const processAjaxEventResponse = <T>(value: T, _name: string): boolean | T => {
  return value;
};

export const processStringObj = (
  value: string,
  key: string,
):
  | TApiaFieldPropsObj
  | string
  | number
  | TFieldScriptEvents
  | TFieldServerEvents => {
  if (key === 'properties') {
    return parseFieldPropertiesObj(jsonDecode(value)) as TApiaFieldPropsObj;
  }
  if (key === 'scriptEvents') {
    return JSON.parse(value) as TFieldScriptEvents;
  }
  if (key === 'serverEvents') {
    return JSON.parse(value) as TFieldServerEvents;
  }
  if (key === 'gridXml') {
    return getJSObjfromXML(he.decode(value).replaceAll('&', '&amp;'));
  }
  if (key === 'value' && typeof value === 'number' && Number.isNaN(value)) {
    return '';
  }
  if (key === 'valueType' && typeof value === 'string' && value === 'null') {
    return '';
  }
  if (
    (key === 'cols' ||
      key === 'rows' ||
      key === 'x' ||
      key === 'y' ||
      key === 'length') &&
    typeof value === 'string'
  ) {
    return parseNumbers(value);
  }

  return value.match?.(/[&]/)
    ? new DOMParser().parseFromString(
        value.replaceAll('<', '&lt;'),
        'text/html',
      ).body.textContent!
    : value;
};

function replaceLabelHtmlValues(o: object) {
  for (const [key, value] of Object.entries(o)) {
    if (typeof value === 'object' && value) {
      (o as any)[key] = replaceLabelHtmlValues(value);
    } else if (typeof value === 'string' && value.match(/&/)) {
      if ((o as any).fieldType === 'editor') {
        (o as any)[key] = new DOMParser().parseFromString(
          value.replaceAll('&', '&amp;').replaceAll('<', '&lt;'),
          'text/html',
        ).body.textContent!;
      } else {
        (o as any)[key] = new DOMParser().parseFromString(
          value.replaceAll('<', '&lt;'),
          'text/html',
        ).body.textContent!;
      }
    }
  }
  return o;
}
export const asyncGetJSObjfromXML = async <T>(xml: string): Promise<T> => {
  const parser = new xml2js.Parser({
    trim: true,
    normalize: true,
    explicitRoot: false,
    mergeAttrs: true,
    explicitArray: false,
    charkey: 'label',
    attrValueProcessors: [
      parseBooleans,
      processStringObj,
      processAjaxEventResponse,
    ],
    tagNameProcessors: [processAjaxEventTagNames],
    attrNameProcessors: [processAjaxEventAttrNames],
  } as ParserOptions);

  const result = await new Promise<T>((resolve, reject) => {
    parser.parseString(xml, (err: Error | null, innerResult: T) => {
      if (err) reject(err);
      else {
        resolve(
          typeof innerResult === 'object' && innerResult
            ? (replaceLabelHtmlValues(innerResult) as any)
            : innerResult,
        );
      }
    });
  });

  return result;
};

export const getJSObjfromXML = (
  xml: XMLDocument | string,
): Record<string, unknown> => {
  let objJS;

  const parser = new xml2js.Parser({
    trim: true,
    normalize: true,
    explicitRoot: false,
    mergeAttrs: true,
    explicitArray: false,
    charkey: 'label',
    attrValueProcessors: [
      parseBooleans,
      processStringObj,
      processAjaxEventResponse,
    ],
    tagNameProcessors: [processAjaxEventTagNames],
    attrNameProcessors: [processAjaxEventAttrNames],
  } as ParserOptions);

  parser.parseString(
    xml,
    (err: Error | null, result: Record<string, unknown>) => {
      if (err) {
        console.warn('Error Parsing XML data', err);
        objJS = err;
      } else {
        if (result?.code === '-1' && result.exceptions) {
          const exceptions = result?.exceptions as TSessionExceptions;
          console.warn(result);
          store.dispatch(
            updateSessionState({
              isLog: false,
              message: exceptions.exception.label,
            }),
          );
        }
        objJS = result;
      }
    },
  );
  if (objJS) {
    dispatchNotifications(objJS);
    return typeof objJS === 'object' && objJS
      ? (replaceLabelHtmlValues(objJS) as any)
      : objJS;
  }
  return {};
};

export const parseXMLRequestResponse = (
  response: AxiosResponse<unknown, unknown>,
): Record<string, unknown> => {
  let params = {};
  if (response.config.data) {
    params = parse(response.config.data as string);
  }
  const requestResponse = response.data;
  const xmlToJSOBj = getJSObjfromXML(requestResponse as XMLDocument);
  if (ApiaApi.getConfig().debug)
    console.log(
      `%c Request:`,
      'color:blue;font-size:1em;font-weight: bold',
      response.config.url,
      '\n',
      { params },
      `Response:`,
      xmlToJSOBj,
    );
  if (xmlToJSOBj) {
    return xmlToJSOBj;
  }
  return {};
};

// format messages with tokens
export const formatMessage = (
  str: string,
  obj: { [key: string]: string },
): string => {
  let newStr = str;
  Object.entries(obj).forEach(([key, value]) => {
    let placeHolder;
    if (value !== '') {
      placeHolder = `<${key}>`;
    } else {
      placeHolder = `"<${key}>"`;
    }
    if (newStr?.includes(placeHolder)) {
      newStr = newStr.replace(placeHolder, value || ' ');
    }
  });
  return newStr;
};

export const arrayOrArray = convertToArray;

export const getOutOfSyncFields = (
  id?: string,
): (TApiaFieldValuesObj | null)[] => {
  const fields = Object.values(propsStore.getAllFields());
  return fields?.filter((field, index) => {
    if (
      index > 0 &&
      id !== field?.id &&
      field?.isSynched !== true &&
      field?.field.fieldType !== Field.TYPE_FILE &&
      field?.field.fieldType !== Field.TYPE_CAPTCHA
    ) {
      return field;
    }
    return null;
  });
};

export function noNaN(number: unknown, defaultReturn = 0) {
  const returnNumber = Number(number);
  if (number === null || Number.isNaN(returnNumber)) return defaultReturn;
  return returnNumber;
}

export function clog(parameters: string, ...elements: unknown[]) {
  const result = parameters.match(/(\w+)\/(.*)/);
  const color = result === null ? 'white' : result[1];
  const title = result === null ? 'NoTitle' : result[2];
  console.log(`%c${title}`, `color: ${color}`, ...elements);
}

/**
 * This function gets an array of elements of any kind and an array of
 * functions which will be called to test conditions and will return element
 * whose index equals the matching condition's index
 *
 * @param arr An array of elements of any kind
 * @param conditions An array of conditions, which will be tested in order to determine which index of the array will be returned
 * @param defaultIndex The return value in case that no condition matches
 * @returns An element of the array if any of the conditions matches or the defaultIndex otherwise
 */
export function getIndex<T = unknown>(
  arr: T[],
  conditions: (boolean | (() => boolean))[],
  defaultIndex?: number,
): T {
  for (let i = 0; i < conditions.length; i++) {
    if (
      (typeof conditions[i] === 'boolean' && conditions[i]) ||
      (isFunction(conditions[i]) && (conditions[i] as () => boolean)())
    )
      return arr[i];
  }
  return arr[defaultIndex ?? -1];
}

export function defaultGetNameFromResponse(blob: AxiosResponse) {
  const contentDisposition = blob.headers['content-disposition'] as string;

  return (
    contentDisposition?.match(/filename=(?:\\?")?([^"]+)/i)?.[1] ?? 'noFileName'
  );
}

const secondParameterIsTDownloadUrlOptions = (
  param:
    | string
    | ((blob: AxiosResponse) => string)
    | TDownloadUrlOptions = defaultGetNameFromResponse,
): param is TDownloadUrlOptions => {
  return !!(param as TDownloadUrlOptions).postData;
};

export type TDownloadUrlOptions = {
  fileName?: string;
  getNameFromResponse?: boolean | ((blob: AxiosResponse) => string);
  postData: string;
};
/**
 * This function gets an URL and a file name and performs a file download.
 *
 * @param url The url to fetch
 * @param getNameFromResponse The name will be given by the 'content-disposition' prop in the headers
 */
export async function downloadUrl(
  url: string,
  getNameFromResponse?: (blob: AxiosResponse) => string,
): Promise<void>;
/**
 * This function gets an URL and a file name and performs a file download.
 *
 * @param url The url to fetch
 * @param fileName The name the file will have as default in the save dialog
 */
export async function downloadUrl(
  url: string,
  fileName: string | TDownloadUrlOptions,
): Promise<void>;

export async function downloadUrl(
  url: string,
  secondParameter:
    | string
    | ((blob: AxiosResponse) => string)
    | TDownloadUrlOptions = defaultGetNameFromResponse,
): Promise<void> {
  if (secondParameterIsTDownloadUrlOptions(secondParameter)) {
    const blob = await axios.post(
      url,
      {
        postData: secondParameter.postData,
      },
      { responseType: 'blob' },
    );

    const getNameFromResponse =
      secondParameter.getNameFromResponse instanceof Function
        ? secondParameter.getNameFromResponse
        : secondParameter.getNameFromResponse === true
          ? defaultGetNameFromResponse
          : () => 'noname';

    const actualFileName =
      secondParameter.fileName ?? getNameFromResponse(blob);

    FileSaver.saveAs(new Blob([blob.data]), actualFileName);
  } else {
    const blob = await axios.get(url, {
      responseType: 'blob',
    });

    const actualFileName =
      typeof secondParameter === 'string'
        ? secondParameter
        : secondParameter(blob);

    FileSaver.saveAs(new Blob([blob.data]), actualFileName);
  }
}

/**
 * This function is mainly used in Apia. In some cases a form must perform a
 * fileDownload and to do so it is given as target a download Iframe, which
 * because of Apia methodology, will set a cookie when the download has
 * finished. This function checks the status of that Iframe and calls the
 * callback when the download has started
 *
 * @param iframe An HTMLIframeElement
 * @param callback The callback function to be called when the Iframe finishes
 */
export function detectIframeDownloadFinish(
  iframe: HTMLIFrameElement,
  callback: () => void,
) {
  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (iframeDoc?.readyState === 'complete') {
    /* Este comportamiento es copiado de Apia */
    setTimeout(callback, 150);
    return;
  }
  /* Este también */
  window.setTimeout(() => detectIframeDownloadFinish(iframe, callback), 100);
}

/**
 * Permite ejecutar una espera falsa. Sirve sobre todo para probar el
 * comportamiento de los componentes en situaciones donde la red esté lenta por
 * ejemplo.
 *
 * @example
 *
 * <Button onClick={() => {
 *   (async function mockLoading() {
 *     setIsLoading(true);
 *     await fakeWait(1500);
 *     setIsLoading(false);
 *   })();
 * }} ... />
 */
export function fakeWait(ms: number, result?: unknown) {
  return new Promise((resolve) => {
    /**
     * Esta función se usaba para hacer pruebas, ya no tiene gran utilidad
     * realmente pero no tiene sentido borrarla porque solamente ocupa 3
     * líneas
     * */
    setTimeout(() => resolve(result), ms);
  });
}

export const generateKey = (
  salt: string,
  passPhrase: string,
  keySize: number,
  iterationCount: number,
) => {
  const key = CryptoJS.PBKDF2(passPhrase, CryptoJS.enc.Hex.parse(salt), {
    keySize: keySize / 32,
    iterations: iterationCount,
  });
  return key;
};

export const encrypt = (
  salt: string,
  iv: string,
  passPhrase: string,
  plainText: string,
  keySize: number,
  iterationCount: number,
) => {
  const key = generateKey(salt, passPhrase, keySize, iterationCount);
  const encrypted = CryptoJS.AES.encrypt(plainText, key, {
    iv: CryptoJS.enc.Hex.parse(iv),
  });
  return encrypted.ciphertext.toString(CryptoJS.enc.Base64);
};

export const decrypt = (
  salt: string,
  iv: string,
  passPhrase: string,
  cipherText: string,
  keySize: number,
  iterationCount: number,
) => {
  const key = generateKey(salt, passPhrase, keySize, iterationCount);
  const cipherParams = CryptoJS.lib.CipherParams.create({
    ciphertext: CryptoJS.enc.Base64.parse(cipherText),
  });
  const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
    iv: CryptoJS.enc.Hex.parse(iv),
  });
  return decrypted.toString(CryptoJS.enc.Utf8);
};

/**
 * Parses a mask which has (or not) the Apia mask's format and returns
 * a react-input-mask formated mask.
 *
 * @param mask The Apia-format (or not) mask
 */
export function parseMask(mask = '') {
  return mask.replaceAll(/n/g, '9').replaceAll(/'([^']+)'/g, '$1');
}

/**
 * With this method it is possible to determine wether an event is a
 * CustomEvent or not.
 */
export function isCustomEvent<CustomEventType>(
  event: Event,
): event is CustomEvent<CustomEventType> {
  return 'detail' in event;
}

/**
 * Este método itera sobre un objeto hasta encontrar la ruta especificada
 * 
 * @example
 * 
  const obj = {
    a: {
      b: {
        c: {
          d: "d"
        }
      }
    }
  };

  console.log(getValueByPath(obj,'a.b.c')) // { d: 'd' }
  console.log(getValueByPath(obj,'a.b.c.d')) // 'd'
 * 
 */
export function getValueByPath(
  obj: Record<string, unknown>,
  path: string | string[],
  separator = '.',
): unknown {
  const actualPath = typeof path === 'string' ? path.split(separator) : path;
  if (typeof obj !== 'object' || !obj) {
    if (actualPath.length === 0) return obj;
    return undefined;
  }
  const currentStep = actualPath.shift() as string;
  if (actualPath.length === 0) return obj[currentStep];
  return getValueByPath(
    obj[currentStep] as Record<string, unknown>,
    actualPath,
    separator,
  );
}

export function setValueByPath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
) {
  const steps = path.split('.');
  if (steps.length === 0) {
    console.warn(`An empty path was provoided ${path}`);
    return null;
  }
  const originalClonedObject = clone(obj ?? {});
  let currentObj = originalClonedObject;
  for (let i = 0; i < steps.length - 1; i++) {
    if (!currentObj[steps[i]]) currentObj[steps[i]] = {};
    if (typeof currentObj[steps[i]] === 'object') {
      currentObj = currentObj[steps[i]] as Record<string, unknown>;
    } else {
      console.info({
        originalObject: obj,
        originalPath: path,
        currentObj,
        currentStep: steps[i],
      });
      throw new Error(
        `The provided path ${path} cannot be applied due to it is not an object's path.`,
      );
    }
  }
  currentObj[steps.pop() as string] = value;
  return originalClonedObject;
}

export const toBoolean = (value: unknown) => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return !!value;
};

/**
 * Recibe un texto como parámetro en format **snakeCase** y lo convierte en
 * **kebab-case**
 */
export function snakeToKebab(text: string) {
  return text
    .replaceAll(/([A-Z]|^)[a-z]+/g, (match) => {
      return `${match[0].toLowerCase() + match.slice(1)}__SEPARATE__`;
    })
    .split('__SEPARATE__')
    .slice(0, -1)
    .join('-');
}

export interface IParameter {
  name: string;
  value: unknown;
  allowMultiple?: boolean;
}

export class Url {
  base: string;

  #parameters: Record<string, string | string[]> = {};

  constructor(
    baseUrl: string,
    public defaultAllowMultiple = true,
  ) {
    const [base, query] = baseUrl.split('?');
    this.base = base;
    query?.split('&').forEach((current) => {
      const [name, ...value] = current.split('=');
      this.addParameter(name, value.join('='));
    });
  }

  addParameter(name: string, value: unknown, allowMultiple?: boolean) {
    if (
      (allowMultiple === undefined && !this.defaultAllowMultiple) ||
      allowMultiple === false
    )
      this.#parameters[name] = String(value);
    else
      this.#parameters[name] = this.#parameters[name]
        ? [...arrayOrArray(this.#parameters[name]), String(value)]
        : [String(value)];
  }

  addParameters(parameters: IParameter[]) {
    parameters.forEach((current) =>
      this.addParameter(current.name, current.value, current.allowMultiple),
    );
  }

  getParameter(name: string) {
    return this.#parameters[name];
  }

  toString() {
    const parametersArray = Object.entries(this.#parameters);

    return `${this.base}${
      parametersArray.length > 0
        ? `?${parametersArray
            .map(([name, value]) =>
              Array.isArray(value)
                ? value.map((current) => `${name}=${current}`).join('&')
                : `${name}=${String(value)}`,
            )
            .join('&')}`
        : ''
    }`;
  }
}

declare global {
  interface Window {
    labels: {
      [key: string]: {
        text: string;
        tooltip: string;
      };
    };
  }
}

/**
 * Dado un nombre de etiqueta, devuelve el texto que esa etiqueta contiene en
 * el idioma actual. En este momento, lo único que hace realmente esta función
 * es devolver la variable del window con el mismo nombre que se está pidiendo.
 *
 * La idea de implementar esta función, es que en un futuro la fuente de la que
 * provienen las labels pueda ser más diversa.
 */
export function getLabel(
  name: string,
  replaceTokens?: {
    text?: Record<string, string>;
    title?: Record<string, string>;
  },
) {
  const label = window.labels[name] ?? {
    text: `Not preloaded: ${name}`,
    tooltip: `Not preloaded: ${name}`,
  };

  if (replaceTokens?.text)
    label.text = formatMessage(label.text, replaceTokens.text);
  if (replaceTokens?.title)
    label.tooltip = formatMessage(label.tooltip, replaceTokens.title);

  return label;
}
